import { HttpErrorResponse } from '@angular/common/http';
import { Observable, OperatorFunction, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiResponse } from '../interfaces/guarantor-onboarding.interfaces';

function toError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }
  if (err != null && typeof err === 'object') {
    const msg = formatGuarantorErrorPayload(err);
    if (msg) {
      return new Error(msg);
    }
    try {
      return new Error(JSON.stringify(err));
    } catch {
      return new Error('Request failed');
    }
  }
  return new Error(String(err));
}

/** RxJS operator: map 404 to `null`, rethrow mapped errors for other statuses. */
export function catch404AsNull<T>(): OperatorFunction<T, T | null> {
  return catchError((err: unknown): Observable<T | null> => {
    if (err instanceof HttpErrorResponse && err.status === 404) {
      return of(null);
    }
    if (err instanceof HttpErrorResponse) {
      return throwError(() => mapGuarantorHttpError(err));
    }
    return throwError(() => toError(err));
  });
}

/** RxJS operator: map HttpErrorResponse to a readable Error. */
export function catchGuarantorMappedError<T>(): OperatorFunction<T, T> {
  return catchError((err: unknown) => {
    if (err instanceof HttpErrorResponse) {
      return throwError(() => mapGuarantorHttpError(err));
    }
    return throwError(() => toError(err));
  });
}

export function isApiResponseEnvelope<T>(value: unknown): value is ApiResponse<T> {
  if (typeof value !== 'object' || value === null || !('data' in value)) {
    return false;
  }
  const v = value as Record<string, unknown>;
  const s = v['success'];
  // LMS may use boolean or numeric 1; treat as envelope only when success is truthy
  const successOk = s === true || s === 1;
  return successOk && typeof v['data'] !== 'undefined';
}

/**
 * Unwraps `{ data, success }` or returns raw body when the API returns DTOs without an envelope.
 */
export function unwrapGuarantorBody<T>(body: ApiResponse<T> | T): T {
  if (isApiResponseEnvelope<T>(body)) {
    return body.data;
  }
  return body as T;
}

/**
 * LMS POST/PUT/DELETE often return `{ success, data, message }`. Keeps `message` for success toasts.
 */
export interface GuarantorMutationResult<T> {
  data: T;
  message?: string;
}

export function unwrapGuarantorMutationResult<T>(
  body: ApiResponse<T> | T | null | undefined
): GuarantorMutationResult<T> {
  if (body === null || body === undefined) {
    return { data: undefined as T };
  }
  if (isApiResponseEnvelope<T>(body)) {
    const raw = body as unknown as Record<string, unknown>;
    const msg = raw['message'] ?? raw['Message'];
    return {
      data: body.data,
      message: typeof msg === 'string' && msg.trim() ? msg.trim() : undefined,
    };
  }
  return { data: body as T };
}

export interface GuarantorHttpErrorShape {
  message?: string | unknown;
  title?: string;
  data?: unknown;
  errors?: Record<string, string[]>;
  errorCode?: string;
}

function pickFirstString(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
}

/**
 * Normalizes LMS API error bodies (camelCase + .NET PascalCase), nested `error`, and `data` object/array.
 * Avoids `[object Object]` in toasts.
 */
export function formatGuarantorErrorPayload(payload: unknown): string {
  if (payload == null) return '';
  if (typeof payload === 'string') {
    const t = payload.trim();
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        return formatGuarantorErrorPayload(JSON.parse(t) as unknown);
      } catch {
        return t;
      }
    }
    return t;
  }
  if (typeof payload !== 'object') return String(payload);

  const p = payload as Record<string, unknown>;
  const chunks: string[] = [];

  const topMessage = pickFirstString(p, ['message', 'Message', 'detail', 'Detail', 'title', 'Title']);
  if (topMessage) {
    chunks.push(topMessage);
  }

  const errProp = p['error'];
  if (typeof errProp === 'string' && errProp.trim()) {
    chunks.push(errProp.trim());
  } else if (errProp != null && typeof errProp === 'object') {
    const nested = formatGuarantorErrorPayload(errProp);
    if (nested) {
      chunks.push(nested);
    }
  }

  const pMsg = p['message'] ?? p['Message'];
  if (pMsg != null && typeof pMsg !== 'string') {
    const serialized =
      typeof pMsg === 'object' ? JSON.stringify(pMsg) : String(pMsg);
    if (serialized && serialized !== '{}' && !chunks.some((c) => c.includes(serialized))) {
      chunks.push(serialized);
    }
  }

  const pData = p['data'] ?? p['Data'];
  if (Array.isArray(pData)) {
    const lines = pData
      .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
      .filter((s) => s && String(s).trim());
    if (lines.length) {
      chunks.push(...lines);
    }
  } else if (typeof pData === 'string' && pData.trim()) {
    chunks.push(pData.trim());
  } else if (pData != null && typeof pData === 'object' && !Array.isArray(pData)) {
    const inner = formatGuarantorErrorPayload(pData);
    if (inner) {
      chunks.push(inner);
    }
  }

  const pErrors = p['errors'] ?? p['Errors'];
  if (pErrors && typeof pErrors === 'object' && !Array.isArray(pErrors)) {
    const errLines = Object.entries(pErrors as Record<string, unknown>).flatMap(([k, v]) =>
      Array.isArray(v)
        ? v.map((s) => `${k}: ${String(s)}`)
        : [`${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`]
    );
    if (errLines.length) {
      chunks.push(errLines.join('; '));
    }
  }

  if (chunks.length) {
    return [...new Set(chunks.filter(Boolean))].join('\n');
  }
  return '';
}

/**
 * Builds a human-readable Error from an HttpErrorResponse (validation or server).
 */
export function mapGuarantorHttpError(err: HttpErrorResponse): Error {
  const payload = err.error;
  let msg = formatGuarantorErrorPayload(payload);
  if (!msg) {
    msg = err.message || `Request failed (${err.status})`;
  }
  return new Error(msg);
}

/**
 * Safe message for toaster error callbacks (Error, HttpErrorResponse, or raw API body).
 */
export function getGuarantorErrorMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    return mapGuarantorHttpError(err).message;
  }
  if (err instanceof Error && typeof err.message === 'string' && err.message.trim()) {
    const m = err.message.trim();
    if (m !== '[object Object]') {
      return m;
    }
  }
  const fromPayload = formatGuarantorErrorPayload(err);
  if (fromPayload) {
    return fromPayload;
  }
  if (typeof err === 'object' && err !== null) {
    try {
      return JSON.stringify(err);
    } catch {
      return 'Request failed';
    }
  }
  return String(err ?? '');
}

/**
 * Safe success/detail text when API returns `message` or nested `data.message` (avoids `[object Object]`).
 */
export function formatGuarantorSuccessMessage(res: unknown): string {
  if (res == null) return '';
  if (typeof res === 'string') return res.trim();
  if (typeof res !== 'object') return String(res);

  const r = res as Record<string, unknown>;
  const rMsg = r['message'];
  if (typeof rMsg === 'string' && rMsg.trim()) {
    return rMsg.trim();
  }
  if (rMsg != null && typeof rMsg !== 'string') {
    return typeof rMsg === 'object' ? JSON.stringify(rMsg) : String(rMsg);
  }

  const data = r['data'];
  if (data && typeof data === 'object' && data !== null) {
    const d = data as Record<string, unknown>;
    const dMsg = d['message'];
    if (typeof dMsg === 'string' && dMsg.trim()) {
      return dMsg.trim();
    }
  }
  return '';
}

/**
 * Success toast text for `GuarantorMutationResult`: prefers API envelope `message`, then DTO-only fields.
 */
export function guarantorMutationToastMessage<T>(result: GuarantorMutationResult<T>): string {
  if (result.message && result.message.trim()) {
    return result.message.trim();
  }
  if (result.data != null && typeof result.data === 'object') {
    const fromData = formatGuarantorSuccessMessage(result.data);
    if (fromData) return fromData;
  }
  return '';
}
